/**
 * BreakdownItemEditModal — Phase 13 (2026-07)
 *
 * Full-field edit modal for a `breakdown_items` row (prop chip).
 * Superset of the chip's inline description-edit flow; opened from the
 * Chip's ⋮ menu ("Edit…"). Used by both ArtByScene and ArtByCharacter
 * tabs — one modal, no duplication.
 *
 * Fields exposed (all patchable via `PATCH /breakdown-items/{id}`):
 *   description, character_name, notes, source, asset_sub_type,
 *   is_flagged, category, youtube_url, video_refs, link_refs, asset_id
 *
 * Meta-fields intentionally NOT exposed: id, created_at, updated_at,
 * origin, ai_confidence, scene_id, project_id, script_id, is_deleted,
 * is_action_prop (separate promote/demote flow), image_paths (existing
 * chip photo flow untouched).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, AlertTriangle, Plus, Trash2, Link2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { AuthImage } from '../../components/AuthImage';
import NotesPhotoStrip from './NotesPhotoStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors backend/core.py BreakdownCategory enum.
const CATEGORY_OPTIONS = [
  { v: 'PROP',                label: 'PROP (Action Props)' },
  { v: 'SCENE_PROP',          label: 'SCENE_PROP (Scene Props)' },
  { v: 'ART_SET_DRESSING',    label: 'ART_SET_DRESSING (Set Dressing)' },
  { v: 'PICTURE_VEHICLE',     label: 'PICTURE_VEHICLE' },
  { v: 'COSTUME',             label: 'COSTUME' },
  { v: 'VEHICLE',             label: 'VEHICLE' },
  { v: 'ANIMAL',              label: 'ANIMAL' },
  { v: 'EXTRA_BACKGROUND',    label: 'EXTRA_BACKGROUND' },
  { v: 'MAKEUP_HAIR',         label: 'MAKEUP_HAIR' },
  { v: 'SFX_SPECIAL_EFFECTS', label: 'SFX_SPECIAL_EFFECTS' },
  { v: 'VFX',                 label: 'VFX' },
  { v: 'STUNT',               label: 'STUNT' },
  { v: 'SOUND_MUSIC',         label: 'SOUND_MUSIC' },
  { v: 'PRODUCTION_NOTE',     label: 'PRODUCTION_NOTE' },
];

// Free-form on backend, presets per BreakdownItem model docstrings.
const SOURCE_PRESETS   = ['purchase', 'rent', 'make', 'other'];
const SUB_TYPE_PRESETS = ['weapon', 'set_decoration', 'prop', 'other'];

const EMPTY = {
  description: '', character_name: '', notes: '', source: '',
  asset_sub_type: '', is_flagged: false, category: 'PROP',
  youtube_url: '', video_refs: [], link_refs: [], asset_id: '',
};

// Repeater for a list-of-URL field (video_refs / link_refs).
function UrlList({ label, value, onChange, testKey }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">{label}</div>
      <div className="space-y-1">
        {(value || []).map((u, i) => (
          <div key={i} className="flex items-center gap-1">
            <input value={u}
              onChange={(e) => {
                const next = [...value];
                next[i] = e.target.value;
                onChange(next);
              }}
              className="flex-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-xs"
              placeholder="https://…"
              data-testid={`${testKey}-input-${i}`} />
            <button type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="text-[var(--color-text-dim)] hover:text-[var(--color-priority-critical)] p-1"
              title="Remove"
              data-testid={`${testKey}-remove-${i}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button type="button"
          onClick={() => onChange([...(value || []), ''])}
          className="text-[10px] text-[var(--color-accent)] hover:underline flex items-center gap-1"
          data-testid={`${testKey}-add`}>
          <Plus className="w-3 h-3" /> add URL
        </button>
      </div>
    </div>
  );
}

// Searchable single-select for asset_id. Fetches once, filters client-side.
function AssetPicker({ projectId, token, value, onChange }) {
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!projectId || !token) return;
    let ok = true;
    axios.get(`${API}/projects/${projectId}/art/assets`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { limit: 500 },
    }).then(r => {
      if (!ok) return;
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setAssets(items);
    }).catch(() => setAssets([]));
    return () => { ok = false; };
  }, [projectId, token]);

  const selected = useMemo(() => assets.find(a => a.id === value), [assets, value]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return assets.slice(0, 25);
    return assets.filter(a => (a.name || '').toLowerCase().includes(s)).slice(0, 25);
  }, [assets, q]);

  return (
    <div>
      <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1 flex items-center gap-1">
        <Link2 className="w-3 h-3" /> Linked asset (optional)
      </div>
      {value && selected ? (
        <div className="flex items-center gap-2 bg-[var(--color-bg)] border border-[var(--color-accent-border)] rounded px-2 py-1.5">
          <Link2 className="w-3 h-3" style={{ color: 'var(--color-accent)' }} />
          <span className="text-xs flex-1" data-testid="bim-asset-linked-name">{selected.name}</span>
          <button type="button"
            onClick={() => onChange('')}
            className="text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-priority-critical)]"
            data-testid="bim-asset-unlink">
            Unlink
          </button>
        </div>
      ) : (
        <div className="relative">
          <input value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-xs"
            placeholder={value ? '(loading asset…)' : 'Search assets by name…'}
            data-testid="bim-asset-search" />
          {open && filtered.length > 0 && (
            <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto bg-[var(--color-panel)] border border-[var(--color-border)] rounded shadow-lg">
              {filtered.map(a => (
                <button key={a.id} type="button"
                  onMouseDown={() => { onChange(a.id); setQ(''); setOpen(false); }}
                  className="w-full text-left text-xs px-2 py-1 hover:bg-[var(--color-panel-alt)]"
                  data-testid={`bim-asset-option-${a.id}`}>
                  {a.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BreakdownItemEditModal({
  open, item, projectId, token, onClose, onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [imagePaths, setImagePaths] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      description:     item.description || '',
      character_name:  item.character_name || '',
      notes:           item.notes || '',
      source:          item.source || '',
      asset_sub_type:  item.asset_sub_type || '',
      is_flagged:      !!item.is_flagged,
      category:        item.category || 'PROP',
      youtube_url:     item.youtube_url || '',
      video_refs:      Array.isArray(item.video_refs) ? [...item.video_refs] : [],
      link_refs:       Array.isArray(item.link_refs)  ? [...item.link_refs]  : [],
      asset_id:        item.asset_id || '',
    });
    setImagePaths(Array.isArray(item.image_paths) ? [...item.image_paths] : []);
  }, [open, item]);

  const uploadPhoto = useCallback(async (file) => {
    if (!file || !item?.id) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/breakdown-items/${item.id}/images`, fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      // Backend POST returns {storage_path, url}. Append to local state.
      if (r.data?.storage_path) {
        setImagePaths(prev => [...prev, r.data.storage_path]);
      }
      toast.success('Photo attached');
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    } finally {
      setUploadingImg(false);
    }
  }, [item?.id, token]);

  const deletePhoto = useCallback(async (path) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(
        `${API}/breakdown-items/${item.id}/images`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { path },
        },
      );
      setImagePaths(prev => prev.filter(p => p !== path));
      toast.success('Photo deleted');
    } catch (err) {
      console.error(err);
      toast.error('Delete failed');
    }
  }, [item?.id, token]);

  if (!open || !item) return null;

  const categoryChanged = form.category !== (item.category || 'PROP');

  const save = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      // Trim URLs, drop empty entries.
      const payload = {
        description:     form.description.trim(),
        character_name:  form.character_name.trim() || null,
        notes:           form.notes.trim() || null,
        source:          form.source.trim() || null,
        asset_sub_type:  form.asset_sub_type.trim() || null,
        is_flagged:      !!form.is_flagged,
        category:        form.category,
        youtube_url:     form.youtube_url.trim() || null,
        video_refs:      form.video_refs.map(s => s.trim()).filter(Boolean),
        link_refs:       form.link_refs.map(s => s.trim()).filter(Boolean),
        asset_id:        form.asset_id || null,
      };
      const r = await axios.patch(
        `${API}/breakdown-items/${item.id}`, payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved?.({ ...item, ...payload }, r?.data);
      toast.success('Item updated');
      onClose?.();
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || 'Update failed';
      toast.error(typeof detail === 'string' ? detail : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
         data-testid="breakdown-item-edit-modal">
      <div className="w-[720px] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg">
        <header className="sticky top-0 flex items-center gap-2 px-4 py-3 bg-[var(--color-bg)] border-b border-[var(--color-border)]">
          <span className="text-sm font-bold text-[var(--color-accent)]">Edit item</span>
          <span className="text-[10px] text-[var(--color-text-dim)]">· {item.id.slice(0, 8)}</span>
          <button onClick={onClose}
            className="ml-auto text-[var(--color-text-dim)] hover:text-white"
            data-testid="breakdown-item-edit-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {/* description */}
          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Description *</div>
            <input value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm"
              placeholder="e.g. Antique brass kettle"
              data-testid="bim-description" />
          </div>

          {/* category with warning */}
          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Category</div>
            <select value={form.category}
              onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-sm"
              data-testid="bim-category">
              {CATEGORY_OPTIONS.map(o => (
                <option key={o.v} value={o.v}>{o.label}</option>
              ))}
            </select>
            {categoryChanged && (
              <div className="mt-1 flex items-center gap-1 text-[10px]"
                   style={{ color: 'var(--color-priority-critical)' }}
                   data-testid="bim-category-warning">
                <AlertTriangle className="w-3 h-3" />
                Recategorising will move this item to a different column.
              </div>
            )}
          </div>

          {/* character_name + is_flagged */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Character name</div>
              <input value={form.character_name}
                onChange={(e) => setForm(f => ({ ...f, character_name: e.target.value }))}
                className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-xs"
                placeholder="For costumes / character-scoped props"
                data-testid="bim-character-name" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Flagged</div>
              <label className="flex items-center gap-2 py-1.5 cursor-pointer">
                <input type="checkbox"
                  checked={form.is_flagged}
                  onChange={(e) => setForm(f => ({ ...f, is_flagged: e.target.checked }))}
                  data-testid="bim-is-flagged" />
                <span className="text-xs">Mark this item as flagged</span>
              </label>
            </div>
          </div>

          {/* source + asset_sub_type — preset select w/ "other" custom */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Source</div>
              <div className="flex gap-1 flex-wrap">
                {SOURCE_PRESETS.map(s => (
                  <button key={s} type="button"
                    onClick={() => setForm(f => ({ ...f, source: s }))}
                    className={`text-[10px] px-2 py-1 rounded border ${form.source === s ? 'border-[var(--color-accent)] text-[var(--color-accent)]' : 'border-[var(--color-border)] text-[var(--color-text-dim)]'}`}
                    data-testid={`bim-source-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
              <input value={SOURCE_PRESETS.includes(form.source) ? '' : form.source}
                onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}
                className="mt-1 w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-xs"
                placeholder="Or custom value…"
                data-testid="bim-source-custom" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Asset sub-type</div>
              <div className="flex gap-1 flex-wrap">
                {SUB_TYPE_PRESETS.map(s => (
                  <button key={s} type="button"
                    onClick={() => setForm(f => ({ ...f, asset_sub_type: s }))}
                    className={`text-[10px] px-2 py-1 rounded border ${form.asset_sub_type === s ? 'border-[var(--color-accent)] text-[var(--color-accent)]' : 'border-[var(--color-border)] text-[var(--color-text-dim)]'}`}
                    data-testid={`bim-subtype-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
              <input value={SUB_TYPE_PRESETS.includes(form.asset_sub_type) ? '' : form.asset_sub_type}
                onChange={(e) => setForm(f => ({ ...f, asset_sub_type: e.target.value }))}
                className="mt-1 w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-xs"
                placeholder="Or custom value…"
                data-testid="bim-subtype-custom" />
            </div>
          </div>

          {/* notes */}
          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Notes</div>
            <textarea value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1.5 text-xs"
              placeholder="Free-form notes distinct from the description"
              data-testid="bim-notes" />
            {/* Ask 3 — image attach for chip `notes` via mood_board scope */}
            <NotesPhotoStrip
              projectId={projectId} token={token}
              scope="item_notes" scopeId={item?.id}
              testKey="bim-notes-photos" />
          </div>

          {/* Ask 2 — full multi-photo gallery for this chip's image_paths */}
          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">Photos ({imagePaths.length})</div>
            <div className="flex gap-1 flex-wrap items-center" data-testid="bim-photos">
              {imagePaths.map(p => (
                <div key={p} className="relative group w-[80px] h-[80px] rounded overflow-hidden border border-[var(--color-border)]">
                  <AuthImage path={p} className="w-full h-full object-cover" alt="" />
                  <button type="button"
                    onClick={() => deletePhoto(p)}
                    className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/70 text-[var(--color-priority-critical)] rounded p-0.5"
                    title="Delete photo"
                    data-testid={`bim-photo-delete-${p.split('/').pop()}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingImg}
                className="w-[80px] h-[80px] rounded border border-dashed border-[var(--color-border-strong)] hover:border-[var(--color-accent)] text-[var(--color-text-dim)] hover:text-[var(--color-accent)] flex flex-col items-center justify-center gap-1 text-[10px] disabled:opacity-40"
                data-testid="bim-photo-upload">
                <Upload className="w-3 h-3" />
                {uploadingImg ? '…' : 'Add photo'}
              </button>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value=''; uploadPhoto(f); }} />
            </div>
          </div>

          {/* youtube_url */}
          <div>
            <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-1">YouTube URL</div>
            <input value={form.youtube_url}
              onChange={(e) => setForm(f => ({ ...f, youtube_url: e.target.value }))}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-1 text-xs"
              placeholder="https://youtube.com/…"
              data-testid="bim-youtube-url" />
          </div>

          {/* video_refs + link_refs */}
          <div className="grid grid-cols-2 gap-3">
            <UrlList label="Video refs"
              value={form.video_refs}
              onChange={(v) => setForm(f => ({ ...f, video_refs: v }))}
              testKey="bim-video-refs" />
            <UrlList label="Link refs"
              value={form.link_refs}
              onChange={(v) => setForm(f => ({ ...f, link_refs: v }))}
              testKey="bim-link-refs" />
          </div>

          {/* asset_id */}
          <AssetPicker projectId={projectId} token={token}
            value={form.asset_id}
            onChange={(v) => setForm(f => ({ ...f, asset_id: v }))} />
        </div>

        <footer className="sticky bottom-0 flex items-center justify-end gap-2 px-4 py-3 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
          <button onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-[var(--color-border)] text-[var(--color-text-dim)] hover:text-white"
            data-testid="breakdown-item-edit-modal-cancel"
            disabled={saving}>
            Cancel
          </button>
          <button onClick={save}
            className="text-xs px-3 py-1.5 rounded bg-[var(--color-accent)] text-black font-bold disabled:opacity-60"
            data-testid="breakdown-item-edit-modal-save"
            disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}
